import { PayloadAction } from '@reduxjs/toolkit';
import type { Action } from 'redux';
import {
  call,
  delay,
  put,
  race,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';

import { apiCallAsync } from 'src/redux/api/saga';
import { showNotification } from 'src/utils';
import {
  AI_POLL_BACKOFF,
  AI_POLL_INTERVAL_MAX_MS,
  AI_POLL_INTERVAL_START_MS,
  AI_POLL_TIMEOUT_MS,
  AI_PROMPT_STATUS_URL,
  AI_PROMPT_TEMPLATES_URL,
  AI_PROMPT_URL,
  AI_TEMPLATES_TOAST_ID,
} from './constants';
import { markdownToHtml, markdownToInline } from './markdown';
import {
  clearPreview,
  generateDraftFailure,
  generateDraftRequest,
  generateDraftSuccess,
  rejectPreview,
  rephraseFailure,
  rephraseRequest,
  rephraseSuccess,
} from './slice';
import {
  fetchTemplatesFailure,
  fetchTemplatesRequest,
  fetchTemplatesSuccess,
  selectPromptTemplates,
} from './templatesSlice';
import {
  AiActionKind,
  AiNarrativeContext,
  AiNarrativeIdPayload,
  AiPromptStatusResponse,
  AiSubmitPromptResponse,
  FetchTemplatesRequestPayload,
  GenerateDraftRequestPayload,
  RephraseRequestPayload,
} from './types';
import {
  asTemplateList,
  fillTemplate,
  isFailedStatus,
  isPendingStatus,
  pickTemplate,
  promptVariables,
  readApiResponse,
  readGeneratedText,
  readUsedDocuments,
} from './utils';

export function* FetchPromptTemplatesSaga(
  action: PayloadAction<FetchTemplatesRequestPayload>,
) {
  const { key, narrativeType } = action.payload;
  try {
    const response = yield call(apiCallAsync, AI_PROMPT_TEMPLATES_URL, {
      narrative_type: narrativeType,
    });
    const items = asTemplateList(readApiResponse(response, 'Prompt templates'));
    yield put(fetchTemplatesSuccess({ key, items }));
  } catch (e) {
    yield put(
      fetchTemplatesFailure({
        key,
        error: e.message || 'Prompt templates failed',
      }),
    );
    showNotification('Unable to load the AI prompt templates', 'error', {
      toastId: AI_TEMPLATES_TOAST_ID,
    });
    console.error(e);
  }
}

export function* PollPromptStatusSaga(eventId: number | string) {
  let waited = 0;
  let interval = AI_POLL_INTERVAL_START_MS;

  for (;;) {
    const response = yield call(apiCallAsync, AI_PROMPT_STATUS_URL, {
      event_id: eventId,
    });
    const payload = readApiResponse(
      response,
      'Prompt status',
    ) as AiPromptStatusResponse;

    if (isFailedStatus(payload?.status)) {
      throw new Error(`Prompt status: the job failed (${payload.status})`);
    }

    const text = readGeneratedText(payload);
    if (text && !isPendingStatus(payload?.status)) {
      return payload;
    }

    if (waited + interval > AI_POLL_TIMEOUT_MS) {
      throw new Error(
        `Prompt status: still pending after ${Math.round(
          waited / 1000,
        )}s, giving up`,
      );
    }

    yield delay(interval);
    waited += interval;
    interval = Math.min(
      Math.round(interval * AI_POLL_BACKOFF),
      AI_POLL_INTERVAL_MAX_MS,
    );
  }
}

export function* RequestAiCompletionSaga(
  context: AiNarrativeContext,
  actionKind: AiActionKind,
  extras: { currentContent?: string; selectedText?: string },
) {
  const templates = yield select(selectPromptTemplates(context.narrativeType));
  const template = pickTemplate(templates.items, actionKind);
  const prompt = fillTemplate(template, promptVariables(context, extras));

  const response = yield call(
    apiCallAsync,
    AI_PROMPT_URL,
    { prompt_text: prompt, year: context.year, csp_code: context.cspCode },
    'post',
  );
  const submission = readApiResponse(
    response,
    'Prompt submission',
  ) as AiSubmitPromptResponse;
  if (!submission?.event_id) {
    throw new Error(
      `Prompt submission: no event_id in the response (fields: ${
        Object.keys(submission || {}).join(', ') || 'none'
      })`,
    );
  }

  return yield call(PollPromptStatusSaga, submission.event_id);
}

const abandonedNarrative =
  (narrativeId: number) =>
  (candidate: Action): boolean => {
    const { type, payload } = candidate as PayloadAction<AiNarrativeIdPayload>;
    return (
      (type === clearPreview.type || type === rejectPreview.type) &&
      payload?.narrativeId === narrativeId
    );
  };

export function* RunDraftGenerationSaga(
  action: PayloadAction<GenerateDraftRequestPayload>,
) {
  const {
    narrativeId,
    currentContent,
    action: actionKind,
    context,
  } = action.payload;

  const completed = yield call(RequestAiCompletionSaga, context, actionKind, {
    currentContent,
  });

  const draftHtml = markdownToHtml(readGeneratedText(completed));
  if (!draftHtml) {
    throw new Error('The model returned an empty draft');
  }

  yield put(
    generateDraftSuccess({
      narrativeId,
      draftHtml,
      references: readUsedDocuments(completed),
    }),
  );
}

export function* GenerateDraftSaga(
  action: PayloadAction<GenerateDraftRequestPayload>,
) {
  const { narrativeId } = action.payload;
  try {
    yield race({
      done: call(RunDraftGenerationSaga, action),
      cancelled: take(abandonedNarrative(narrativeId)),
    });
  } catch (e) {
    yield put(
      generateDraftFailure({
        narrativeId,
        error: e.message || 'Draft generation failed',
      }),
    );
    showNotification(`Unable to generate the AI draft: ${e.message}`, 'error', {
      toastId: `ai-draft-${narrativeId}`,
    });
    console.error(e);
  }
}

export function* RunRephraseSaga(
  action: PayloadAction<RephraseRequestPayload>,
) {
  const {
    narrativeId,
    selectedText,
    currentContent,
    action: actionKind,
    context,
  } = action.payload;

  const completed = yield call(RequestAiCompletionSaga, context, actionKind, {
    currentContent,
    selectedText,
  });

  const proposedText = markdownToInline(readGeneratedText(completed));
  if (!proposedText) {
    throw new Error('The model returned an empty suggestion');
  }

  yield put(rephraseSuccess({ narrativeId, proposedText }));
}

export function* RephraseSaga(action: PayloadAction<RephraseRequestPayload>) {
  const { narrativeId } = action.payload;
  try {
    yield race({
      done: call(RunRephraseSaga, action),
      cancelled: take(abandonedNarrative(narrativeId)),
    });
  } catch (e) {
    yield put(
      rephraseFailure({
        narrativeId,
        error: e.message || 'Rephrase failed',
      }),
    );
    showNotification(
      `Unable to rephrase the selected text: ${e.message}`,
      'error',
      { toastId: `ai-rephrase-${narrativeId}` },
    );
    console.error(e);
  }
}

export default function* aiNarrativeSaga() {
  yield takeEvery(generateDraftRequest.type, GenerateDraftSaga);
  yield takeEvery(rephraseRequest.type, RephraseSaga);
  yield takeEvery(fetchTemplatesRequest.type, FetchPromptTemplatesSaga);
}
